import { create } from 'zustand';
import Router from 'next/router';
import posthog from 'posthog-js';
import { googleLogout } from '@react-oauth/google';

import {
  getAuth,
  removeAuth,
  getCurrentTeamToken,
  setCurrentTeamToken,
  removeCurrentTeamToken,
} from '@services/identity.service';

import {
  generateTeamToken,
  verifyTeamToken,
  getUserAccountById,
  getUserTeams,
  restrictCopilot,
} from '@services/youtube-platform.service';

import type { PurchasePlanSettings } from '@components/PricingPlans';

export interface TeamContext {
  oid: string | null;
  tid?: string;
  name?: string;
  utid?: string;
  role?: string;
}

export interface Team {
  id: string;
  role: string;
  email?: string;
  billing_account: {
    id: string;
    name: string;
    owner_id: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
}

export interface TeamUser {
  user_role?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const detailRoutes = [
  '/my-teams/[teamId]',
  '/zoom/[platformId]',
  '/youtube/[platformId]',
  '/teams/[platformId]',
  '/ipa/[platformId]',
  '/stats/[streamId]',
  '/streams/[streamId]/prepare',
];

// Vanilla BroadcastChannel used by doLogout (no React hook needed).
let _broadcastChannel: BroadcastChannel | null = null;
export function getAuthBroadcastChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return null;
  if (!_broadcastChannel) {
    _broadcastChannel = new window.BroadcastChannel('authStateChanged');
  }
  return _broadcastChannel;
}

export interface TeamsState {
  // --- state ---
  teams: Team[];
  user: TeamUser | undefined;
  ownTeam: Team | undefined;
  currentTeam: Team | undefined;
  currentTeamBillingAccountOwnerID: string | undefined;
  teamContext: TeamContext;
  teamMessages: string[];
  teamErrors: string[];
  modal: boolean;
  newTeam: Team | undefined;
  isLoading: boolean;
  isClient: boolean;
  celloUcc: string | null;
  /** Synced from usePurchasePlan by TeamsDataProvider */
  settings: PurchasePlanSettings | null;

  // --- pure setters ---
  setTeams: (teams: Team[]) => void;
  setUser: (user: TeamUser | undefined) => void;
  setOwnTeam: (team: Team | undefined) => void;
  setCurrentTeam: (team: Team | undefined) => void;
  setTeamContext: (ctx: TeamContext | undefined) => void;
  setTeamMessages: (msgs: string[]) => void;
  setTeamErrors: (errs: string[]) => void;
  setModal: (open: boolean) => void;
  toggleModal: () => void;
  setNewTeam: (team: Team | undefined) => void;
  setIsLoading: (v: boolean) => void;
  setIsClient: (v: boolean) => void;
  setCelloUcc: (ucc: string | null) => void;
  setSettings: (s: PurchasePlanSettings | null) => void;

  // --- plain actions (no hook deps) ---
  updateTeam: (updatedTeam: Partial<Team['billing_account']> & { id: string }) => void;
  filterTeamsByRole: (role: string, negate?: boolean) => Team[];
  isSelected: (id: string) => boolean;
  isTeamAdmin: () => boolean;
  isTeamOwner: () => boolean;
  isTeamModerator: () => boolean;
  isLoggedIn: () => boolean;
  getBrandLogo: (defaultImageUrl: string) => string;
  isLogoRequired: () => boolean;
  initTeam: () => Promise<void>;
  switchTeam: (team: Team) => void;
  selectTeam: (team: Team) => void;
  doLogout: () => Promise<void>;
  checkIsCopilot: () => Promise<void>;
}

export const useTeamsStore = create<TeamsState>()((set, get) => ({
  teams: [],
  user: undefined,
  ownTeam: undefined,
  currentTeam: undefined,
  currentTeamBillingAccountOwnerID: undefined,
  teamContext: { oid: null },
  teamMessages: [],
  teamErrors: [],
  modal: false,
  newTeam: undefined,
  isLoading: true,
  isClient: false,
  celloUcc: null,
  settings: null,

  // --- setters ---
  setTeams: (teams) => set({ teams }),
  setUser: (user) => set({ user }),
  setOwnTeam: (ownTeam) => set({ ownTeam }),
  setCurrentTeam: (currentTeam) =>
    set({
      currentTeam,
      currentTeamBillingAccountOwnerID: currentTeam?.billing_account?.owner_id,
    }),
  setTeamContext: (teamContext) => set({ teamContext: teamContext ?? { oid: null } }),
  setTeamMessages: (teamMessages) => set({ teamMessages }),
  setTeamErrors: (teamErrors) => set({ teamErrors }),
  setModal: (modal) => set({ modal }),
  toggleModal: () => set((s) => ({ modal: !s.modal })),
  setNewTeam: (newTeam) => set({ newTeam }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setIsClient: (isClient) => set({ isClient }),
  setCelloUcc: (celloUcc) => set({ celloUcc }),
  setSettings: (settings) => set({ settings }),

  // --- plain actions ---
  updateTeam: (updatedTeam) =>
    set((s) => {
      const patch = (t: any) => ({ ...t, billing_account: { ...t.billing_account, ...updatedTeam } });
      const isMatch = (t: any) => t.billing_account.id === updatedTeam.id;
      return {
        teams: s.teams.map((t) => (isMatch(t) ? patch(t) : t)),
        currentTeam: s.currentTeam && isMatch(s.currentTeam) ? patch(s.currentTeam) : s.currentTeam,
      };
    }),

  filterTeamsByRole: (role, negate = false) =>
    get().teams.filter((t) => (negate ? t.role !== role : t.role === role)),

  isSelected: (id) => id === get().currentTeam?.billing_account?.id,
  isTeamAdmin: () => !!get().currentTeam?.role?.match('^(owner|editor)$'),
  isTeamOwner: () => !!get().currentTeam?.role?.match('^(owner)$'),
  isTeamModerator: () => !!get().currentTeam?.role?.match('^(owner|editor|moderator)$'),
  isLoggedIn: () => !!get().user && get().user?.user_role !== 'guest',

  getBrandLogo: (defaultImageUrl) => {
    const billingAccount = get().currentTeam?.billing_account;
    if (billingAccount?.purchase_plan?.custom_branding_allowed) {
      return billingAccount.brand_logo ?? defaultImageUrl;
    }
    return defaultImageUrl;
  },

  isLogoRequired: () => {
    const billingAccount = get().currentTeam?.billing_account;
    if (billingAccount?.purchase_plan?.no_branding_allowed) {
      return billingAccount.is_branding_required !== false;
    }
    return true;
  },

  initTeam: async () => {
    if (!getAuth()) {
      set({ isLoading: false });
      return;
    }

    const [userResponse, teamsResponse] = await Promise.all([
      getUserAccountById(),
      getUserTeams(),
    ]);

    if (userResponse.status && userResponse.entity) {
      set({ user: userResponse.entity });
    }

    const teams: Team[] = teamsResponse.status && teamsResponse.entity
      ? teamsResponse.entity
      : [];

    if (teams.length > 0) {
      set({ teams });

      // Determine which team to activate (mirrors original getCurrentTeam logic)
      const own = teams.find((t: Team) => t.role === 'owner');
      set({ ownTeam: own });

      const teamToken = getCurrentTeamToken();
      let activeTeam: Team | undefined;

      if (teamToken) {
        const teamData = await verifyTeamToken(teamToken);
        if (teamData?.entity) {
          activeTeam = teams.find((t: Team) => t.billing_account.id === teamData.entity.tid);
        }
      }

      const target = activeTeam ?? own;
      if (target) get().switchTeam(target);
      get().checkIsCopilot();
    }

    set({ isLoading: false });
  },

  switchTeam: (team) => {
    const { teamContext } = get();
    if (team.billing_account.owner_id === teamContext?.oid) return;

    generateTeamToken(team.billing_account.id).then(
      (data: { status: boolean; entity: { token: string } }) => {
        if (!data.status) return;
        setCurrentTeamToken(data.entity.token);
        set({
          teamContext: {
            name: team.billing_account.name,
            tid: team.billing_account.id,
            oid: team.billing_account.owner_id,
            utid: team.id,
            role: team.role,
          },
          currentTeam: team,
          currentTeamBillingAccountOwnerID: team.billing_account.owner_id,
        });
      }
    );
  },

  selectTeam: (team) => {
    const route = detailRoutes.find((r) => r === Router.pathname);
    if (route) {
      set({ newTeam: team, modal: true });
    } else {
      get().switchTeam(team);
    }
  },

  doLogout: async () => {
    try { posthog.capture('Logged Out'); } catch { /* posthog may not be initialised */ }
    googleLogout();
    await removeAuth();
    removeCurrentTeamToken();
    set({ currentTeam: undefined, teamContext: { oid: null } });
    localStorage.removeItem('template_id');
    getAuthBroadcastChannel()?.postMessage('logout');
  },

  checkIsCopilot: async () => {
    const res = await restrictCopilot(Router.pathname);
    if (res.status) return;
    get().doLogout();
  },
}));

export const useIsLogoRequired = (): boolean =>
  useTeamsStore((s) => {
    const ba = s.currentTeam?.billing_account;
    if (ba?.purchase_plan?.no_branding_allowed) {
      return ba.is_branding_required !== false;
    }
    return true;
  });

export const useBrandLogo = (defaultImageUrl: string): string =>
  useTeamsStore((s) => {
    const ba = s.currentTeam?.billing_account;
    if (ba?.purchase_plan?.custom_branding_allowed) {
      return ba.brand_logo ?? defaultImageUrl;
    }
    return defaultImageUrl;
  });

// ---------------------------------------------------------------------------
// Vanilla cross-tab logout listener (client-only).
// The "login" case (PowerPoint re-auth) is handled by TeamsGate which has
// access to the useOffice hook.
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
  const listenerChannel = new window.BroadcastChannel('authStateChanged');
  listenerChannel.onmessage = async (e: MessageEvent) => {
    if (e.data === 'logout') {
      await removeAuth();
      removeCurrentTeamToken();
      const { isProtected } = await import('@lib/security');
      if (isProtected(window.location.pathname)) {
        Router.push('/login');
      }
    }
  };
}
